import {
    IMedia,
    IProps,
    IRef,
} from './types';

import classNames from 'classnames';
import React, {
    forwardRef,
    useImperativeHandle,
    useState,
} from 'react';

import {
    chooseFiles,
} from 'helpers/functions';

import {
    MAX_FILE_SIZE,
} from 'entities/medias/constants';

import {
    SimpleButton,
} from 'components/buttons';
import {
    ErrorMessage,
} from 'components/other';

import MediaViewerModal from './MediaViewerModal';

import {
    ERROR,
} from './constants';
import {
    formatError,
} from './functions';

import styles from './MediasInput.module.scss';

const MediasInput = forwardRef<IRef, IProps>((props, ref) => {
    const [medias, setMedias] = useState<IMedia[]>(props.value);
    const [error, setError] = useState<ERROR | null>(null);
    const [isPending, setIsPending] = useState(false);
    const [viewingMediaUrl, setViewingMediaUrl] = useState<string | null>(null);

    const isDisabled = isPending || !!props.isDisabled;

    const onUpload = async () => {
        if (isDisabled || !props.createMedia) {
            return;
        }
        if (medias.length >= props.maxCount) {
            setError(ERROR.TOO_MANY);

            return;
        }

        const [file] = await chooseFiles(['image'], false);

        if (!file) {
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError(ERROR.TOO_LARGE);

            return;
        }

        setIsPending(true);

        const mediaId = await props.createMedia(file);

        setIsPending(false);

        if (!mediaId) {
            return;
        }

        const nextMedias = [
            ...medias,
            {
                mediaId,
                url: URL.createObjectURL(file),
            },
        ];

        setMedias(nextMedias);
        setError(null);
        props.onChange?.(nextMedias);
    };
    const onRemove = (mediaId: string) => {
        if (isDisabled) {
            return;
        }

        const nextMedias = medias.filter((media) => {
            return media.mediaId !== mediaId;
        });

        setMedias(nextMedias);
        setError(null);
        props.onChange?.(nextMedias);
    };

    useImperativeHandle(ref, () => {
        return {
            getValue(): string[] {
                return medias.map((media) => {
                    return media.mediaId;
                });
            },
            validate(): boolean {
                if (props.isRequired && !medias.length) {
                    setError(ERROR.IS_REQUIRED);

                    return false;
                }

                setError(null);

                return true;
            },
        };
    });

    return (
        <div className={classNames(styles.mediasInput, props.className)}>
            {
                !!props.label &&
                <div className={styles.title}>
                    {props.label}
                </div>
            }
            <div className={styles.list}>
                {
                    medias.map((media) => (
                        <div
                            key={media.mediaId}
                            className={styles.item}
                        >
                            <img
                                className={styles.image}
                                src={media.url}
                                alt={''}
                                onClick={() => setViewingMediaUrl(media.url)}
                            />
                            {
                                !isDisabled &&
                                <button
                                    className={styles.removeButton}
                                    type={'button'}
                                    onClick={() => onRemove(media.mediaId)}
                                >
                                    ×
                                </button>
                            }
                        </div>
                    ))
                }
                {
                    !isDisabled &&
                    !!props.createMedia &&
                    medias.length < props.maxCount &&
                    <SimpleButton
                        className={styles.button}
                        onClick={onUpload}
                    >
                        Upload
                    </SimpleButton>
                }
            </div>
            <ErrorMessage
                className={styles.error}
                text={formatError(error)}
            />
            <MediaViewerModal
                isOpen={!!viewingMediaUrl}
                mediaUrl={viewingMediaUrl}
                onClose={() => setViewingMediaUrl(null)}
            />
        </div>
    );
});

export default MediasInput;
